const DEFAULT_ASPECT_RATIO = 16 / 9;

const isFinitePositive = (value) => Number.isFinite(value) && value > 0;
const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

const toRelativeRect = (rect, layoutRect) => ({
    left: rect.left - layoutRect.left,
    top: rect.top - layoutRect.top,
    right: rect.right - layoutRect.left,
    bottom: rect.bottom - layoutRect.top,
});

/**
 * Selects a 16:9 viewport centred on the active phidgets instead of shrinking
 * the complete empty dashboard. The returned zone is always inside the layout
 * and remains a truthful crop of the actual screen.
 */
export const getDashboardThumbnailBounds = (layoutRect, itemRects, options = {}) => {
    const aspectRatio = options.aspectRatio || DEFAULT_ASPECT_RATIO;
    const padding = Number.isFinite(options.padding) ? options.padding : 28;
    const minimumHeight = Number.isFinite(options.minimumHeight) ? options.minimumHeight : 260;

    if (!layoutRect || !isFinitePositive(layoutRect.width) || !isFinitePositive(layoutRect.height)) {
        return null;
    }

    const visibleRects = (itemRects || [])
        .filter((rect) => rect
            && Number.isFinite(rect.left)
            && Number.isFinite(rect.right)
            && Number.isFinite(rect.top)
            && Number.isFinite(rect.bottom)
            && rect.right > layoutRect.left
            && rect.left < layoutRect.right
            && rect.bottom > layoutRect.top
            && rect.top < layoutRect.bottom)
        .map((rect) => toRelativeRect(rect, layoutRect));

    if (!visibleRects.length) {
        const width = Math.ceil(layoutRect.width);
        const height = Math.ceil(Math.min(layoutRect.height, width / aspectRatio));
        return {left: 0, top: 0, width, height};
    }

    const content = visibleRects.reduce((bounds, rect) => ({
        left: Math.min(bounds.left, rect.left),
        top: Math.min(bounds.top, rect.top),
        right: Math.max(bounds.right, rect.right),
        bottom: Math.max(bounds.bottom, rect.bottom),
    }), {
        left: layoutRect.width,
        top: layoutRect.height,
        right: 0,
        bottom: 0,
    });

    const contentWidth = Math.max(1, content.right - content.left + padding * 2);
    const contentHeight = Math.max(minimumHeight, content.bottom - content.top + padding * 2);
    let height = Math.min(layoutRect.height, Math.max(contentHeight, contentWidth / aspectRatio));
    let width = height * aspectRatio;

    if (width > layoutRect.width) {
        width = layoutRect.width;
        height = width / aspectRatio;
    }

    const centerX = (content.left + content.right) / 2;
    const centerY = (content.top + content.bottom) / 2;
    const left = clamp(centerX - width / 2, 0, Math.max(0, layoutRect.width - width));
    const top = clamp(centerY - height / 2, 0, Math.max(0, layoutRect.height - height));

    return {
        left: Math.round(left),
        top: Math.round(top),
        width: Math.ceil(width),
        height: Math.ceil(height),
    };
};

export const buildScreenThumbnailUrl = (apiUrl, totemId, version) => {
    const root = String(apiUrl || '').replace(/\/$/, '');
    const cacheVersion = version ? encodeURIComponent(String(version)) : 'current';
    return `${root}/storage/totems/${encodeURIComponent(totemId)}.png?v=${cacheVersion}`;
};
